import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {ScopedCommands} from '@github-ui/ui-commands'
import {useSyntheticChange} from '@github-ui/use-synthetic-change'
import {useFormControlForwardedProps, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import type React from 'react'
import {
  Children,
  cloneElement,
  type CSSProperties,
  type Ref,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'

import {AutocompleteSuggestions} from './AutocompleteSuggestions'
import styles from './InlineAutocomplete.module.css'
import type {
  SelectSuggestionsEvent,
  ShowSuggestionsEvent,
  Suggestion,
  Suggestions,
  SuggestionsPlacement,
  TextInputCompatibleChild,
  TextInputElement,
  Trigger,
} from './types'
import {
  assertInputElement,
  augmentHandler,
  calculateSuggestionsQuery,
  getSelectionStart,
  getSuggestionKey,
  getSuggestionValue,
  isNullSuggestion,
} from './utils'

export type InlineAutocompleteProps = {
  /** Ref for the input or textarea element */
  inputRef?: Ref<HTMLInputElement | HTMLTextAreaElement>
  /** Additional class names to apply to the container element */
  containerClassName?: string
  /** Register the triggers that can cause suggestions to appear. */
  triggers: Trigger[]
  /**
   * Called when a valid suggestion query is updated. This should be handled by setting the
   * `suggestions` prop accordingly.
   */
  onShowSuggestions: (event: ShowSuggestionsEvent) => void

  /**
   * Called when a suggestion is selected.
   *
   * @note This should be used only for performing side effects, not for modifying
   * the inserted text. Do not call `setState` in this handler or the user's cursor
   * position / undo history could be lost.
   */
  onSelectSuggestion?: (event: SelectSuggestionsEvent) => void

  /**
   * Called to determine whether the suggestion text should replace the typed text.
   * If this returns false, the suggestion text will be removed instead of inserted.
   * This is useful for cases where selecting a suggestion should trigger an action
   * rather than insert text.
   */
  shouldReplaceText?: (event: SelectSuggestionsEvent) => boolean

  /** Called when suggestions should be hidden. Set `suggestions` to `null` in this case. */
  onHideSuggestions: () => void
  /**
   * The currently visible list of suggestions. If `loading`, a loading indicator will be
   * shown. If `null` or empty, the list will be hidden. Suggestion sort will be preserved.
   *
   * Typically, this should not contain more than five or so suggestions.
   */
  suggestions: Suggestions | null
  /**
   * If `true`, suggestions will be applied with both `Tab` and `Enter`, instead of just
   * `Enter`. This may be expected behavior for users used to IDEs, but use caution when
   * hijacking browser tabbing capability.
   * @default false
   */
  tabInsertsSuggestions?: boolean
  /**
   * The `AutocompleteTextarea` has a container for positioning the suggestions overlay.
   * This can break some layouts (ie, if the editor must expand with `flex: 1` to fill space)
   * so you can override container styles here. Usually this should not be necessary.
   * `position` may not be overridden.
   */
  style?: Omit<CSSProperties, 'position'>
  // Typing this as such makes it look like a compatible child internally, but it isn't actually
  // enforced externally so we have to resort to a runtime assertion.
  /**
   * An `input` or `textarea` compatible component to extend. A compatible component is any
   * component that forwards a ref and props to an underlying `input` or `textarea` element,
   * including but not limited to `Input`, `TextArea`, `input`, `textarea`, `styled.input`,
   * and `styled.textarea`. If the child is not compatible, a runtime `TypeError` will be
   * thrown.
   */
  children: TextInputCompatibleChild
  /**
   * Control which side of the insertion point the suggestions list appears on by default. This
   * should almost always be `"below"` because it typically provides a better user experience
   * (the most-relevant suggestions will appear closest to the text). However, if the input
   * is always near the bottom of the screen (ie, a chat composition form), it may be better to
   * display the suggestions above the input.
   *
   * In either case, if there is not enough room to display the suggestions in the default direction,
   * the suggestions will appear in the other direction.
   * @default "below"
   */
  suggestionsPlacement?: SuggestionsPlacement
  /**
   * The name of the portal to render the suggestions overlay into. This is useful if you
   * have multiple inline autocomplete components on the same page and you want to ensure
   * the suggestions overlay is rendered in the correct order.
   */
  portalName?: string
  /**
   * If `true`, the input will be rendered as a block element, taking up the full width of
   * its container. This is useful for rendering the input in a `flex` container, for example.
   */
  fullWidth?: boolean
  /** Optional title/name for the suggestions menu.  */
  title?: string
  /**
   * Optional visual (e.g. an icon or shield) rendered inline after the menu
   * title text. Kept separate from `title` so the title stays a plain string
   * for the listbox's accessible name.
   */
  titleTrailingVisual?: React.ReactNode
  /**
   * For simple cases, the standard representation of this widget is as a `listbox` tied to a `combobox`. For more
   * advanced cases (such as multistep menus or if items need to perform side effects), the widget can instead be
   * represented as a `menu` tied to a plain `textbox`.
   */
  asMenu?: boolean
}

const noop = () => {
  // don't do anything
}

/**
 * Isolates the `cloneElement` call. The React Compiler cannot optimize a component that clones an
 * element carrying a ref during render, so containing it in this trivial wrapper keeps
 * `InlineAutocomplete` itself optimizable — the de-opt (and the lint disable) live only here. This
 * render clones a single element, so it is cheap to re-render. The caller passes the injected props
 * (including `ref`, a regular prop in React 19) directly; we forward them straight to the clone.
 */
function ClonedInput({
  __externalInputElement,
  ...injectedProps
}: {
  __externalInputElement: TextInputCompatibleChild
} & (
  | React.DetailedHTMLProps<React.TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>
  | React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>
)) {
  // eslint-disable-next-line @eslint-react/no-clone-element
  return cloneElement(__externalInputElement, injectedProps)
}

/**
 * Shows suggestions to complete the current word/phrase the user is actively typing.
 */
export const InlineAutocomplete = ({
  inputRef: externalInputRef,
  containerClassName = '',
  triggers,
  suggestions,
  onShowSuggestions,
  onHideSuggestions,
  onSelectSuggestion,
  shouldReplaceText,
  style,
  children,
  tabInsertsSuggestions = false,
  suggestionsPlacement = 'below',
  portalName,
  fullWidth = false,
  title: menuTitle,
  titleTrailingVisual: menuTitleTrailingVisual,
  asMenu,
  ...externalInputProps
}: InlineAutocompleteProps & React.ComponentProps<'textarea' | 'input'>) => {
  const inputProps = useFormControlForwardedProps(externalInputProps)

  // Internal ref for reading/controlling the input. A callback ref stores the mounted node and, as
  // soon as it attaches (and again if the consumer swaps the child), validates that the child
  // forwarded its ref to an `<input>`/`<textarea>`. Reading a ref during render is unsafe, so the
  // callback-ref timing (post-commit) is what makes the type check valid — no need to key an effect on
  // the unrelated `children` value. We also merge in any ref the caller passed via `inputRef` and any
  // ref already on the child element, so all receive the node.
  const inputRef = useRef<TextInputElement | HTMLTextAreaElement>(null)
  const attachInput = useCallback((node: TextInputElement | HTMLTextAreaElement | null) => {
    inputRef.current = node
    // Returning a cleanup opts into React 19's ref-cleanup convention (which Primer's `useMergedRefs`
    // propagates), so React runs the cleanup on detach instead of invoking this with `null`. `node` is
    // therefore always the real element here, letting us assert its type unconditionally.
    assertInputElement(node)
    return () => {
      inputRef.current = null
    }
  }, [])
  const mergedInputRef = useMergedRefs(
    attachInput,
    useMergedRefs(
      externalInputRef,
      children.props.ref as React.Ref<TextInputElement | HTMLTextAreaElement> | undefined,
    ),
  )
  // eslint-disable-next-line @eslint-react/no-children-only
  const externalInput = Children.only(children)

  // The callback ref above only fires when the child forwards its ref. If it never does, `inputRef`
  // stays `null` and the component silently no-ops, so we surface that contract violation here.
  useEffect(() => {
    assertInputElement(inputRef.current)
  }, [])

  const emitSyntheticChange = useSyntheticChange({
    inputRef,
    fallbackEventHandler: externalInput.props.onChange ?? noop,
  })

  /** Stores the query that caused the current suggestion list to appear. */
  const [showEvent, setShowEvent] = useState<ShowSuggestionsEvent | null>(null)
  const {isBrowser} = useRuntimeEnvironment()
  const suggestionsVisible = isBrowser && suggestions !== null && suggestions.length > 0

  const [activeSuggestionKey, setActiveSuggestionKey] = useState<string>()
  const activeSuggestion = Array.isArray(suggestions)
    ? (suggestions.find(s => getSuggestionKey(s) === activeSuggestionKey) ?? suggestions[0])
    : undefined

  if (activeSuggestionKey && !suggestionsVisible) setActiveSuggestionKey(undefined)

  // User can blur while suggestions are visible with shift+tab
  const onBlur: React.FocusEventHandler<TextInputElement> = () => {
    onHideSuggestions()
  }

  const navigate = (direction: 'up' | 'down' | 'start' | 'end') => {
    // Suggestions can be visible but not navigable when the the loading state is shown
    // The Array.isArray check is fully redundant but satisfies the typechecker
    if (!activeSuggestion || !Array.isArray(suggestions)) return

    const setActiveSuggestion = (suggestion: Suggestion | undefined) => {
      setActiveSuggestionKey(suggestion ? getSuggestionKey(suggestion) : undefined)
    }

    switch (direction) {
      case 'up':
        setActiveSuggestion(suggestions[suggestions.indexOf(activeSuggestion) - 1] ?? suggestions.at(-1))
        break
      case 'down':
        setActiveSuggestion(suggestions[suggestions.indexOf(activeSuggestion) + 1] ?? suggestions[0])
        break
      case 'start':
        setActiveSuggestion(suggestions[0])
        break
      case 'end':
        setActiveSuggestion(suggestions.at(-1))
        break
    }
  }

  const onKeyDown: React.KeyboardEventHandler<TextInputElement> = event => {
    if (!suggestionsVisible) return

    // We can't do _everything_ with ui-commands, unfortunately. In these cases we don't want to preventDefault and
    // they don't really make sense as commands anyway

    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowLeft':
        onHideSuggestions()
        break
      case 'Tab':
        if (!tabInsertsSuggestions) {
          // onBlur will hide the suggestions too, but it will do it too late and focus will enter the overlay first,
          // which results in focus getting lost
          onHideSuggestions()
          break
        }
    }
  }

  const onChange: React.ChangeEventHandler<TextInputElement> = event => {
    const selectionStart = getSelectionStart(event.currentTarget)
    if (selectionStart === null) {
      onHideSuggestions()
      return
    }

    const query = calculateSuggestionsQuery(triggers, event.currentTarget.value, selectionStart)
    const nextShowEvent: ShowSuggestionsEvent | null = query && {
      ...query,
      target: event.currentTarget,
    }
    setShowEvent(nextShowEvent)

    if (nextShowEvent) {
      onShowSuggestions(nextShowEvent)
    } else if (showEvent) {
      // Only notify the parent to hide when suggestions were actually showing. Typing normal text with no
      // active trigger is the common case; skipping the redundant `onHideSuggestions()` avoids a no-op
      // state update (e.g. `setSuggestionEvent(null)`) on every keystroke.
      onHideSuggestions()
    }
  }

  const onCommit = (suggestion = activeSuggestion) => {
    if (!suggestion) return

    const isMultistep = isNullSuggestion(suggestion)
    const value = getSuggestionValue(suggestion) ?? ''

    if (!inputRef.current || !showEvent) return
    const {query, trigger} = showEvent

    const currentCaretPosition = getSelectionStart(inputRef.current) ?? 0
    const deleteLength = query.length + trigger.triggerChar.length
    const startIndex = currentCaretPosition - deleteLength

    const selectEvent = {suggestion, trigger, query, target: inputRef.current}
    const shouldReplace = shouldReplaceText?.(selectEvent) ?? true

    let replacement: string
    if (shouldReplace) {
      const keepTriggerChar = isMultistep || (trigger.keepTriggerCharOnCommit ?? true)
      const maybeTriggerChar = keepTriggerChar ? trigger.triggerChar : ''

      const insertSpace = !isMultistep && (trigger.insertSpaceOnCommit ?? true)
      const maybeSpace = insertSpace ? ' ' : ''

      replacement = `${maybeTriggerChar}${value}${maybeSpace}`
    } else {
      // Remove the trigger and query text
      replacement = ''
    }

    onHideSuggestions()
    emitSyntheticChange(replacement, [startIndex, startIndex + deleteLength])

    onSelectSuggestion?.(selectEvent)
  }

  const listId = useId()
  const getSuggestionId = (suggestion: Suggestion) => `${listId}-${getSuggestionKey(suggestion)}`

  const asCombobox = !asMenu

  return (
    // Using ui-commands for keybinding resolves conflicts with containing components that may also use ui-commands,
    // since ui-commands always takes priority. Otherwise, if a containing component binds an enter command, for example,
    // it will take priority over the inline autocomplete's enter command, which is not what we want
    <ScopedCommands
      commands={
        suggestionsVisible
          ? {
              // Even though the overlay has an Escape listener, it only works when focus is inside the overlay. In this
              // case, the textarea is focused, so we have to handle closing the overlay
              'github:cancel': () => onHideSuggestions(),
              'github:select': () => onCommit(),
              'github:focus-next': tabInsertsSuggestions ? () => onCommit() : undefined,
              'github:down': () => navigate('down'),
              'github:up': () => navigate('up'),
              'github:go-to-start': () => navigate('start'),
              'github:go-to-end': () => navigate('end'),
            }
          : {}
      }
      className={clsx(styles.container, fullWidth && styles.fullWidth, containerClassName)}
      style={style}
    >
      <ClonedInput
        __externalInputElement={externalInput}
        {...inputProps}
        onBlur={augmentHandler(externalInput.props.onBlur, onBlur)}
        onKeyDownCapture={augmentHandler(externalInput.props.onKeyDownCapture, onKeyDown)}
        onChange={augmentHandler(externalInput.props.onChange, onChange)}
        ref={mergedInputRef}
        role={suggestionsVisible && asCombobox ? 'combobox' : externalInput.props.role}
        aria-expanded={suggestionsVisible && asCombobox ? 'true' : externalInput.props['aria-expanded']}
        aria-activedescendant={
          activeSuggestion ? getSuggestionId(activeSuggestion) : externalInput.props['aria-activedescendant']
        }
        aria-controls={suggestionsVisible && asCombobox ? listId : externalInput.props['aria-controls']}
        aria-owns={suggestionsVisible && asMenu ? listId : externalInput.props['aria-owns']}
        aria-autocomplete={suggestionsVisible && asCombobox ? 'list' : externalInput.props['aria-autocomplete']}
        aria-haspopup={suggestionsVisible && asCombobox ? 'listbox' : externalInput.props['aria-haspopup']}
      />
      <AutocompleteSuggestions
        id={listId}
        suggestions={suggestions}
        inputRef={inputRef}
        onCommit={onCommit}
        onClose={onHideSuggestions}
        showEvent={showEvent}
        visible={suggestionsVisible}
        defaultPlacement={suggestionsPlacement}
        portalName={portalName}
        activeSuggestion={activeSuggestion}
        getSuggestionId={getSuggestionId}
        menuTitle={menuTitle}
        menuTitleTrailingVisual={menuTitleTrailingVisual}
        role={asMenu ? 'menu' : 'listbox'}
        tabInsertsSuggestions={tabInsertsSuggestions}
      />
    </ScopedCommands>
  )
}
